import { useEffect, useRef, useState } from "react";

/** Honour the OS "reduce motion" setting so the page stays usable for everyone. */
export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Distance above the fold at which an element is considered "arrived".
 */
const TRIGGER_GAP = 60;

/** True once the page cannot scroll any further down. */
const atBottom = () =>
  window.innerHeight + window.scrollY >=
  document.documentElement.scrollHeight - 2;

/**
 * True when `node` has reached the trigger line, which includes everything
 * already scrolled past (a negative `top`).
 */
const hasArrived = (node) =>
  node.getBoundingClientRect().top <= window.innerHeight - TRIGGER_GAP ||
  atBottom();

/**
 * Reveals every element carrying a `data-reveal` attribute as it scrolls into
 * view by adding `.is-revealed`. One rAF-throttled sweep drives the whole page,
 * so markup only needs the attribute - no wrapper components around the
 * Bootstrap grid.
 *
 * This is a positional test rather than an IntersectionObserver on purpose: an
 * observer only fires when an element's intersection *changes*, so a jump that
 * lands past a block - an anchor link, the End key, a scroll position restored
 * on reload - never produces a callback for it and leaves it stuck at
 * opacity 0 in plain sight.
 */
export function useRevealOnScroll() {
  useEffect(() => {
    let pending = Array.from(document.querySelectorAll("[data-reveal]"));
    if (!pending.length) return undefined;

    const revealAll = () => {
      pending.forEach((node) => node.classList.add("is-revealed"));
      pending = [];
    };

    if (prefersReducedMotion()) {
      revealAll();
      return undefined;
    }

    let frame = null;

    const detach = () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };

    const sweep = () => {
      frame = null;
      const remaining = [];
      pending.forEach((node) => {
        if (hasArrived(node)) node.classList.add("is-revealed");
        else remaining.push(node);
      });
      pending = remaining;
      if (!pending.length) detach();
    };

    function schedule() {
      if (frame === null) frame = window.requestAnimationFrame(sweep);
    }

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    sweep();

    return () => {
      detach();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);
}

/**
 * Adds `.is-stuck` to the header once the page has scrolled past the hero top.
 *
 * The read is rAF-throttled and the state only changes when the answer flips,
 * so scrolling the page does not push a React render per scroll event.
 */
export function useStickyHeader(offset = 24) {
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    let frame = null;

    const update = () => {
      frame = null;
      const next = window.scrollY > offset;
      setStuck((was) => (was === next ? was : next));
    };

    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
    };
  }, [offset]);

  return stuck;
}

/** Height reserved for the sticky header when scrolling something into view. */
export const HEADER_OFFSET = 96;

/**
 * Turns on native smooth scrolling for as long as the landing page is mounted.
 * Set on the element rather than in the stylesheet because `Landingpage.css` is
 * a global import - the admin screens share the document and should keep their
 * instant scrolling.
 */
export function useSmoothScroll(headerOffset = HEADER_OFFSET) {
  useEffect(() => {
    if (prefersReducedMotion()) return undefined;

    const root = document.documentElement;
    const previousBehavior = root.style.scrollBehavior;
    const previousPadding = root.style.scrollPaddingTop;

    root.style.scrollBehavior = "smooth";
    root.style.scrollPaddingTop = `${headerOffset}px`;

    return () => {
      root.style.scrollBehavior = previousBehavior;
      root.style.scrollPaddingTop = previousPadding;
    };
  }, [headerOffset]);
}

/**
 * Glides to the section with the given id, stopping clear of the sticky header.
 * Passing no id returns to the top of the page.
 */
export function scrollToSection(id, headerOffset = HEADER_OFFSET) {
  const behavior = prefersReducedMotion() ? "auto" : "smooth";

  if (!id) {
    window.scrollTo({ top: 0, behavior });
    return;
  }

  const target = document.getElementById(id);
  if (!target) return;

  const top = target.getBoundingClientRect().top + window.scrollY - headerOffset;
  window.scrollTo({ top: Math.max(0, top), behavior });
}

/** Drifts an element a few pixels against the scroll direction. */
export function useParallax(strength = 0.06, max = 26) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return undefined;

    let frame = null;
    const update = () => {
      frame = null;
      const rect = el.getBoundingClientRect();
      const fromCentre = rect.top + rect.height / 2 - window.innerHeight / 2;
      const shift = Math.max(-max, Math.min(max, -fromCentre * strength));
      el.style.setProperty("--parallax-y", `${shift.toFixed(2)}px`);
    };
    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [strength, max]);

  return ref;
}

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

/**
 * Counts a numeric label up to its final value the first time it is seen.
 * `value` is rendered verbatim once the run finishes, so the copy on screen is
 * always exactly what was authored. Labels whose number is too small to be worth
 * animating (or when motion is reduced) render their final text immediately.
 */
export function CountUp({ value, duration = 1600, className }) {
  const ref = useRef(null);
  const parts = /^(\D*)(\d[\d,]*)(.*)$/.exec(String(value));
  const target = parts ? Number(parts[2].replace(/,/g, "")) : NaN;
  const animatable = Boolean(parts) && Number.isFinite(target) && target >= 10;

  const [display, setDisplay] = useState(() =>
    animatable ? `${parts[1]}0${parts[3]}` : value
  );

  useEffect(() => {
    const el = ref.current;
    if (!el || !animatable) {
      setDisplay(value);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setDisplay(value);
      return undefined;
    }

    const [, prefix, digits, suffix] = parts;
    const grouped = digits.includes(",");
    let frame = null;
    let started = false;

    const run = (startedAt) => {
      const step = (now) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        const current = Math.round(easeOutExpo(progress) * target);
        const shown = grouped ? current.toLocaleString("en-US") : String(current);
        setDisplay(progress === 1 ? value : `${prefix}${shown}${suffix}`);
        if (progress < 1) frame = window.requestAnimationFrame(step);
      };
      frame = window.requestAnimationFrame(step);
    };

    const detach = () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };

    function check() {
      if (started) return;
      const rect = el.getBoundingClientRect();
      // Already scrolled past: show the final figure rather than counting up
      // to it off-screen.
      if (rect.bottom <= 0) {
        started = true;
        detach();
        setDisplay(value);
        return;
      }
      if (hasArrived(el)) {
        started = true;
        detach();
        run(performance.now());
      }
    }

    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    check();

    return () => {
      detach();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration, animatable]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
